import { PlusIcon } from '@heroicons/react/24/outline';
import { lazy, ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import PedWorkspaceGuard from '@/components/shared/PedWorkspaceGuard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy';
import useDeferredUnmount from '@/data/hook/shared/useDeferredUnmount';
import useToggle from '@/data/hook/useToggle';

import { QuestionBankContext } from './types';
import { usePedWorkspaceStatus } from '@/data/context/PedWorkspaceContext';

const QuestionBankModal = lazy(() => import('./QuestionBankModal'));

const EXIT_ANIMATION_MS = 200;

type DefaultTriggerProps = {
  onClick: () => void;
};

const DefaultTrigger = ({ onClick }: DefaultTriggerProps) => {
  const { isLimitedEdition } = usePedWorkspaceStatus();
  const { t } = useTranslation('translation', { keyPrefix: 'questionBank' });

  return (
    <PedWorkspaceGuard className="flex-1">
      <Button
        size="md"
        color="accent"
        variant="soft"
        className="flex-1 w-full"
        onClick={onClick}
        disabled={isLimitedEdition}
      >
        <PlusIcon className="w-4 h-4" />
        {t('open')}
      </Button>
    </PedWorkspaceGuard>
  );
};

type QuestionBankTriggerProps = {
  context: QuestionBankContext;
  children?: (open: () => void) => ReactNode;
};

export default function QuestionBankTrigger({
  context,
  children,
}: QuestionBankTriggerProps) {
  const { isOpen, open, close } = useToggle();
  const shouldRenderModal = useDeferredUnmount(isOpen, EXIT_ANIMATION_MS);

  return (
    <>
      {children ? children(open) : <DefaultTrigger onClick={open} />}
      <ConditionalRenderer condition={shouldRenderModal}>
        <Suspense fallback={null}>
          <QuestionBankModal
            visible={isOpen}
            onClose={close}
            context={context}
          />
        </Suspense>
      </ConditionalRenderer>
    </>
  );
}
